import type { UserPublishResultResponse } from "@apis/http/api";
import {
  UserPublishResultResponsePlatform,
  UserPublishResultResponsePublishMode,
  UserPublishResultResponseStatus,
} from "@apis/generated/openapi";

/**
 * 仅在 dev/staging 使用：覆盖发布流转的全部状态分支，便于在不依赖后端的情况下验证每个 case 的 UI。
 *
 * 8 个 status × terminal/needConfirm 标记的组合（可读对照后端 wiki：
 * https://kalowave.feishu.cn/wiki/MOKNwXrPwikCq5kjEytcFSTnnlh）
 */
export interface MockCase {
  /** 下拉里展示的标签 */
  label: string;
  /** 直接喂给 PostRecordProbe 的轮询结果（短路真实 API） */
  result: UserPublishResultResponse;
}

export const MOCK_CASES: MockCase[] = [
  {
    label: "INIT — Reviewing your video...",
    result: {
      id: -1,
      linkId: -1,
      assetId: -1,
      platform: UserPublishResultResponsePlatform.TIKTOK,
      publishMode: UserPublishResultResponsePublishMode.NOW,
      status: UserPublishResultResponseStatus.INIT,
      terminal: false,
      needConfirm: false,
    },
  },
  {
    label: "UPLOADING — Reviewing your video...",
    result: {
      id: -1,
      linkId: -1,
      assetId: -1,
      platform: UserPublishResultResponsePlatform.TIKTOK,
      publishMode: UserPublishResultResponsePublishMode.NOW,
      status: UserPublishResultResponseStatus.UPLOADING,
      terminal: false,
      needConfirm: false,
    },
  },
  {
    label: "PRE_CHECKING — Analyzing video quality...",
    result: {
      id: -1,
      linkId: -1,
      assetId: -1,
      platform: UserPublishResultResponsePlatform.TIKTOK,
      publishMode: UserPublishResultResponsePublishMode.NOW,
      status: UserPublishResultResponseStatus.PRE_CHECKING,
      terminal: false,
      needConfirm: false,
    },
  },
  {
    label: "PRE_CHECK_SUCCESS (no issues) — keep loading",
    result: {
      id: -1,
      linkId: -1,
      assetId: -1,
      platform: UserPublishResultResponsePlatform.TIKTOK,
      publishMode: UserPublishResultResponsePublishMode.NOW,
      status: UserPublishResultResponseStatus.PRE_CHECK_SUCCESS,
      terminal: false,
      needConfirm: false,
    },
  },
  {
    label: "PRE_CHECK_SUCCESS + needConfirm — Optimization suggestions modal",
    result: {
      id: -1,
      linkId: -1,
      assetId: -1,
      platform: UserPublishResultResponsePlatform.TIKTOK,
      publishMode: UserPublishResultResponsePublishMode.NOW,
      status: UserPublishResultResponseStatus.PRE_CHECK_SUCCESS,
      terminal: false,
      needConfirm: true,
      issues: [
        { risk: "Pirated Content", suggestions: "Your video may include unoriginal content." },
        { risk: "Low Resolution", suggestions: "Upload a higher-quality version (≥720p)." },
      ],
    },
  },
  {
    label: "PRE_CHECK_FAIL (video issue) — Back to Edit",
    result: {
      id: -1,
      linkId: -1,
      assetId: -1,
      platform: UserPublishResultResponsePlatform.TIKTOK,
      publishMode: UserPublishResultResponsePublishMode.NOW,
      status: UserPublishResultResponseStatus.PRE_CHECK_FAIL,
      terminal: true,
      needConfirm: false,
      failCode: "TIKTOK-170001019",
      failReason: "The product doesn't exist.",
    },
  },
  {
    label: "PRE_CHECK_FAIL (auth expired) — Reconnect Account",
    result: {
      id: -1,
      linkId: -1,
      assetId: -1,
      platform: UserPublishResultResponsePlatform.TIKTOK,
      publishMode: UserPublishResultResponsePublishMode.NOW,
      status: UserPublishResultResponseStatus.PRE_CHECK_FAIL,
      terminal: true,
      needConfirm: false,
      failCode: "TIKTOK-105003",
      failReason: "TikTok authorization expired. Please reconnect your account.",
    },
  },
  {
    label: "PUBLISHING — Posting your video...",
    result: {
      id: -1,
      linkId: -1,
      assetId: -1,
      platform: UserPublishResultResponsePlatform.TIKTOK,
      publishMode: UserPublishResultResponsePublishMode.NOW,
      status: UserPublishResultResponseStatus.PUBLISHING,
      terminal: false,
      needConfirm: false,
    },
  },
  {
    label: "SUCCESS — message.success + close modal",
    result: {
      id: -1,
      linkId: -1,
      assetId: -1,
      platform: UserPublishResultResponsePlatform.TIKTOK,
      publishMode: UserPublishResultResponsePublishMode.NOW,
      status: UserPublishResultResponseStatus.SUCCESS,
      terminal: true,
      needConfirm: false,
    },
  },
  {
    label: "FAIL — Retry posting modal",
    result: {
      id: -1,
      linkId: -1,
      assetId: -1,
      platform: UserPublishResultResponsePlatform.TIKTOK,
      publishMode: UserPublishResultResponsePublishMode.NOW,
      status: UserPublishResultResponseStatus.FAIL,
      terminal: true,
      needConfirm: false,
      failCode: "TIKTOK-200001001",
      failReason: "Upload to TikTok failed due to a temporary error.",
    },
  },
];

export const isDebugEnabled = () => {
  return import.meta.env.MODE !== "production" && import.meta.env.MODE !== "staging";
};
